import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faMoneyBillWave, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import VendorPaymentForm from './VendorPaymentForm';
import { formatMoney } from '../shared/formatMoney';
const { rootUrl } = require('../../../config/config');

const VendorLedger = ({ supplierId, supplierName }) => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken');

    const [ledger, setLedger] = useState([]);
    const [balance, setBalance] = useState(0);
    const [pendingInvoices, setPendingInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [filters, setFilters] = useState({ date_from: '', date_to: '' });

    const fetchLedger = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            const res = await axios.get(
                `${rootUrl}/api/vendors/${supplierId}/ledger?${params.toString()}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );
            if (res.data?.success) {
                setLedger(res.data.data || []);
                setBalance(res.data.balance || 0);
            }
        } catch (err) {
            console.error('Error fetching ledger:', err);
        } finally {
            setLoading(false);
        }
    }, [supplierId, filters, authToken]);

    const fetchPendingInvoices = useCallback(async () => {
        try {
            const res = await axios.get(
                `${rootUrl}/api/vendors/${supplierId}/pending-invoices`,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );
            if (res.data?.success) setPendingInvoices(res.data.data || []);
        } catch (err) {
            console.error('Error fetching pending invoices:', err);
        }
    }, [supplierId, authToken]);

    useEffect(() => {
        if (supplierId) {
            fetchLedger();
            fetchPendingInvoices();
        }
    }, [supplierId, filters, fetchLedger, fetchPendingInvoices]);

    const formatDate = (d) => new Date(d).toLocaleDateString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric'
    });

    const getTypeLabel = (type) => ({
        PUR: 'Purchase', PAY: 'Payment',
        CRD: 'Credit', ADJ: 'Adjustment'
    })[type] || type || '-';

    const isOverdue = (dueDate) => dueDate && new Date(dueDate) < new Date();

    return (
        <div className="space-y-4">
            {/* Balance + action bar */}
            <div className="bg-white rounded-2xl shadow-xl p-5">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div>
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Current Balance</p>
                        <div className="flex items-baseline gap-2 flex-wrap">
                            <span className={`text-2xl font-bold tabular-nums ${balance > 0 ? 'text-red-600' : balance < 0 ? 'text-green-600' : 'text-gray-800'}`}>
                                {formatMoney(balance)}
                            </span>
                            {balance > 0 && (
                                <span className="text-[10px] font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">We owe vendor</span>
                            )}
                            {balance < 0 && (
                                <span className="text-[10px] font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">Vendor owes us</span>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {pendingInvoices.length > 0 && (
                            <button
                                onClick={() => setShowPaymentForm(true)}
                                className="flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-sm transition-colors"
                            >
                                <FontAwesomeIcon icon={faMoneyBillWave} className="text-xs" />
                                Record Payment
                            </button>
                        )}
                        <button
                            onClick={() => {}}
                            className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 text-xs font-medium rounded-sm transition-colors"
                        >
                            <FontAwesomeIcon icon={faFileExport} className="text-xs" />
                            Export
                        </button>
                    </div>
                </div>
            </div>

            {/* Pending invoices */}
            {pendingInvoices.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 text-xs" />
                        <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                            {pendingInvoices.length} Pending Invoice{pendingInvoices.length > 1 ? 's' : ''}
                        </span>
                    </div>

                    {/* Desktop */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-amber-200">
                                    <th className="pb-2 text-left text-[11px] font-semibold text-amber-700 uppercase tracking-wide pr-4">Invoice No</th>
                                    <th className="pb-2 text-left text-[11px] font-semibold text-amber-700 uppercase tracking-wide pr-4">Date</th>
                                    <th className="pb-2 text-left text-[11px] font-semibold text-amber-700 uppercase tracking-wide pr-4">Due Date</th>
                                    <th className="pb-2 text-right text-[11px] font-semibold text-amber-700 uppercase tracking-wide pr-4">Amount</th>
                                    <th className="pb-2 text-right text-[11px] font-semibold text-amber-700 uppercase tracking-wide pr-4">Paid</th>
                                    <th className="pb-2 text-right text-[11px] font-semibold text-amber-700 uppercase tracking-wide">Remaining</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-100">
                                {pendingInvoices.map(inv => (
                                    <tr key={inv.id}>
                                        <td className="py-2 pr-4 text-gray-700">{inv.invoice_no || '-'}</td>
                                        <td className="py-2 pr-4 text-gray-600">{formatDate(inv.invoice_date)}</td>
                                        <td className="py-2 pr-4">
                                            <span className={isOverdue(inv.due_date) ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                                                {inv.due_date ? formatDate(inv.due_date) : '-'}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-4 text-right tabular-nums text-gray-700">{formatMoney(inv.debit)}</td>
                                        <td className="py-2 pr-4 text-right tabular-nums text-green-700">{formatMoney(inv.paid)}</td>
                                        <td className="py-2 text-right tabular-nums font-semibold text-red-600">{formatMoney(inv.remaining)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile */}
                    <div className="md:hidden space-y-2">
                        {pendingInvoices.map(inv => (
                            <div key={inv.id} className="bg-white rounded-sm p-3 border border-amber-100">
                                <div className="flex justify-between items-start">
                                    <div className="font-semibold text-xs text-gray-800">{inv.invoice_no || '-'}</div>
                                    <div className="text-xs font-bold text-red-600 tabular-nums">{formatMoney(inv.remaining)}</div>
                                </div>
                                <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                                    <span>{formatDate(inv.invoice_date)}</span>
                                    <span className={isOverdue(inv.due_date) ? 'text-red-600 font-semibold' : ''}>
                                        Due: {inv.due_date ? formatDate(inv.due_date) : '-'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Ledger card */}
            <div className="bg-white rounded-2xl shadow-xl">
                {/* Date filters */}
                <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap gap-3 items-end">
                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">Date From</label>
                        <input
                            type="date"
                            value={filters.date_from}
                            onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))}
                            className="px-2 py-1.5 border border-gray-200 rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">Date To</label>
                        <input
                            type="date"
                            value={filters.date_to}
                            onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))}
                            className="px-2 py-1.5 border border-gray-200 rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                        />
                    </div>
                    {(filters.date_from || filters.date_to) && (
                        <button
                            onClick={() => setFilters({ date_from: '', date_to: '' })}
                            className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-sm transition-colors"
                        >
                            Clear
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="p-10 text-center text-xs text-gray-400">Loading transactions...</div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full table-fixed text-xs min-w-[640px]">
                                <colgroup>
                                    <col className="w-24" />
                                    <col className="w-24" />
                                    <col className="w-28" />
                                    <col className="w-28" />
                                    <col className="w-24" />
                                    <col className="w-28" />
                                    <col className="w-28" />
                                    <col className="w-28" />
                                </colgroup>
                                <thead>
                                    <tr className="border-b border-gray-100 bg-gray-50/60">
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Date</th>
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Type</th>
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Reference</th>
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Invoice No</th>
                                        <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Due Date</th>
                                        <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Debit</th>
                                        <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Credit</th>
                                        <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Balance</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {ledger.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-10 text-center text-xs text-gray-400">No transactions found</td>
                                        </tr>
                                    ) : (
                                        ledger.map(entry => (
                                            <tr key={entry.id} className="hover:bg-gray-50/80 transition-colors">
                                                <td className="px-4 py-2.5 text-gray-600">{formatDate(entry.date)}</td>
                                                <td className="px-4 py-2.5 text-gray-600">{getTypeLabel(entry.trans_type)}</td>
                                                <td className="px-4 py-2.5 text-gray-600 text-xs">
                                                    {entry.expense_id ? (
                                                        <a
                                                            href={`/expenses/${entry.expense_id}`}
                                                            className="text-orange-600 hover:underline font-medium"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {entry.expense_no || `EXP-${entry.expense_id}`}
                                                        </a>
                                                    ) : (entry.invoice_no || '—')}
                                                </td>
                                                <td className="px-4 py-2.5 text-gray-600">{entry.invoice_no || '-'}</td>
                                                <td className="px-4 py-2.5">
                                                    {entry.due_date
                                                        ? <span className={isOverdue(entry.due_date) ? 'text-red-600 font-medium' : 'text-gray-600'}>{formatDate(entry.due_date)}</span>
                                                        : <span className="text-gray-400">-</span>
                                                    }
                                                </td>
                                                <td className={`px-4 py-2.5 text-right tabular-nums ${entry.debit > 0 ? 'text-red-600' : 'text-gray-300'}`}>
                                                    {entry.debit > 0 ? formatMoney(entry.debit) : '-'}
                                                </td>
                                                <td className={`px-4 py-2.5 text-right tabular-nums ${entry.credit > 0 ? 'text-green-600' : 'text-gray-300'}`}>
                                                    {entry.credit > 0 ? formatMoney(entry.credit) : '-'}
                                                </td>
                                                <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${entry.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                    {formatMoney(entry.balance)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile rows */}
                        <div className="md:hidden divide-y divide-gray-100">
                            {ledger.length === 0 ? (
                                <div className="p-10 text-center text-xs text-gray-400">No transactions found</div>
                            ) : (
                                ledger.map(entry => (
                                    <div key={entry.id} className="px-4 py-3">
                                        <div className="flex justify-between items-start gap-2">
                                            <div>
                                                <div className="text-xs font-semibold text-gray-800">{formatDate(entry.date)}</div>
                                                <div className="text-[10px] text-gray-500 mt-0.5">{getTypeLabel(entry.trans_type)}</div>
                                                {entry.invoice_no && <div className="text-[10px] text-gray-400">{entry.invoice_no}</div>}
                                                {entry.expense_id && (
                                                    <a
                                                        href={`/expenses/${entry.expense_id}`}
                                                        className="text-[10px] text-orange-600 hover:underline"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {entry.expense_no || `EXP-${entry.expense_id}`}
                                                    </a>
                                                )}
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <div className={`text-xs font-bold tabular-nums ${entry.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                    {formatMoney(entry.balance)}
                                                </div>
                                                {entry.due_date && (
                                                    <div className={`text-[10px] mt-0.5 ${isOverdue(entry.due_date) ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                                                        Due: {formatDate(entry.due_date)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex justify-between mt-1.5 text-[10px]">
                                            <span className={entry.debit > 0 ? 'text-red-600' : 'text-gray-400'}>
                                                Debit: {entry.debit > 0 ? formatMoney(entry.debit) : '-'}
                                            </span>
                                            <span className={entry.credit > 0 ? 'text-green-600' : 'text-gray-400'}>
                                                Credit: {entry.credit > 0 ? formatMoney(entry.credit) : '-'}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>

            {showPaymentForm && (
                <VendorPaymentForm
                    open={showPaymentForm}
                    onClose={() => setShowPaymentForm(false)}
                    onSuccess={() => {
                        setShowPaymentForm(false);
                        fetchLedger();
                        fetchPendingInvoices();
                    }}
                    supplierId={supplierId}
                    supplierName={supplierName}
                    pendingInvoices={pendingInvoices}
                />
            )}
        </div>
    );
};

export default VendorLedger;
